<template>
  <PrimevueDialog
    :visible="visible"
    modal
    :draggable="false"
    :style="{ width: 'min(980px, 96vw)' }"
    :content-style="{ maxHeight: 'calc(90vh - 7rem)', overflowY: 'auto' }"
    header="Mon profil"
    class="user-profile-dialog"
    @update:visible="handleVisibilityChange"
  >
    <p class="text-muted small mb-3">Informations du compte connecté</p>

    <div v-if="!currentUser" class="alert alert-warning mb-0">Profil indisponible.</div>

    <div v-else class="row g-3">
      <div class="col-lg-4">
        <div class="card border-0 shadow-sm profile-card h-100">
          <div class="card-body">
            <div class="profile-summary">
              <div class="profile-avatar">
                <img
                  v-if="avatarPreview || currentUser.profile_photo_url"
                  :src="avatarPreview || currentUser.profile_photo_url"
                  :alt="currentUser.name || 'Photo de profil'"
                />
                <i v-else class="fa fa-user"></i>
              </div>
              <div class="min-w-0">
                <h2 class="h5 mb-1 text-truncate">{{ currentUser.name || 'Utilisateur' }}</h2>
                <span class="badge text-bg-primary text-capitalize">{{ roleLabel }}</span>
              </div>
            </div>

            <dl class="row mb-0 small profile-list">
              <dt class="col-sm-5">Nom d’utilisateur</dt>
              <dd class="col-sm-7">{{ currentUser.username || '-' }}</dd>
              <dt class="col-sm-5">Site actuel</dt>
              <dd class="col-sm-7">{{ shopName || '-' }}</dd>
              <dt class="col-sm-5">Créé le</dt>
              <dd class="col-sm-7">{{ currentUser.created_at ? $format.date(currentUser.created_at) : '-' }}</dd>
              <dt class="col-sm-5">Statut</dt>
              <dd class="col-sm-7">
                <span class="badge" :class="currentUser.is_active ? 'text-bg-success' : 'text-bg-danger'">
                  {{ currentUser.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </dd>
            </dl>
          </div>
        </div>
      </div>

      <div class="col-lg-8">
        <div v-if="canEditProfile" class="card border-0 shadow-sm profile-card mb-3">
          <div class="card-body">
            <h2 class="h6 mb-3">Informations personnelles</h2>
            <form class="profile-form-grid" @submit.prevent="saveProfile">
              <div>
                <label class="form-label small fw-semibold">Email</label>
                <PrimevueInputText v-model="profileForm.email" type="email" class="w-100" placeholder="email@exemple.com" />
              </div>
              <div>
                <label class="form-label small fw-semibold">Téléphone</label>
                <PrimevueInputText v-model="profileForm.telephone1" type="tel" class="w-100" placeholder="+241..." @input="sanitizePhoneField" />
              </div>
              <div>
                <label class="form-label small fw-semibold">Date de naissance</label>
                <input v-model="profileForm.birth_date" type="date" class="form-control form-control-sm" />
              </div>
              <div>
                <label class="form-label small fw-semibold">Sexe</label>
                <PrimevueDropdown v-model="profileForm.gender" :options="genderOptions" optionLabel="label" optionValue="value" class="w-100" />
              </div>
              <div class="profile-photo-field">
                <label class="form-label small fw-semibold">Photo de profil</label>
                <input type="file" accept="image/jpeg,image/png,image/webp" class="form-control form-control-sm" @change="handlePhotoChange" />
              </div>
              <div class="profile-form-actions">
                <AppLoadingButton type="submit" class="btn btn-primary btn-sm" :loading="isSavingProfile" loading-label="Enregistrement...">
                  <i class="fa fa-save"></i> Enregistrer le profil
                </AppLoadingButton>
              </div>
            </form>
          </div>
        </div>

        <div class="card border-0 shadow-sm profile-card">
          <div class="card-body">
            <h2 class="h6 mb-3">Changer mon mot de passe</h2>
            <form class="profile-form-grid" @submit.prevent="changePassword">
              <div>
                <label class="form-label small fw-semibold">Mot de passe actuel</label>
                <PrimevueInputText v-model="passwordForm.current_password" type="password" class="w-100" required />
              </div>
              <div>
                <label class="form-label small fw-semibold">Nouveau mot de passe</label>
                <p class="small text-muted">12 caractères minimum, avec majuscule, minuscule, chiffre et symbole.</p>
                <PrimevueInputText v-model="passwordForm.new_password" type="password" class="w-100" required minlength="12" maxlength="72" autocomplete="new-password" />
              </div>
              <div>
                <label class="form-label small fw-semibold">Confirmation</label>
                <PrimevueInputText v-model="passwordForm.new_password_confirmation" type="password" class="w-100" required minlength="12" maxlength="72" autocomplete="new-password" />
              </div>
              <div class="profile-form-actions">
                <AppLoadingButton type="submit" class="btn btn-outline-primary btn-sm" :loading="isChangingPassword" loading-label="Enregistrement...">
                  <i class="fa fa-key"></i> Changer le mot de passe
                </AppLoadingButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </PrimevueDialog>
</template>

<script>
import AppLoadingButton from '@/components/AppLoadingButton.vue';
import { isPhoneInputValid, phoneInputValidationMessage, sanitizePhoneInput } from '@/utils/phone';

const emptyPasswordForm = () => ({
  current_password: '',
  new_password: '',
  new_password_confirmation: '',
});

export default {
  name: 'UserProfileDialog',
  components: { AppLoadingButton },
  props: { visible: { type: Boolean, default: false } },
  emits: ['update:visible'],
  data() {
    return {
      profileForm: { email: '', telephone1: '', birth_date: '', gender: '' },
      passwordForm: emptyPasswordForm(),
      photoFile: null,
      avatarPreview: '',
      isSavingProfile: false,
      isChangingPassword: false,
      genderOptions: [
        { label: 'Non renseigné', value: '' },
        { label: 'Femme', value: 'female' },
        { label: 'Homme', value: 'male' },
        { label: 'Autre', value: 'other' },
      ],
    };
  },
  computed: {
    currentUser() { return this.$store.getters['auth/currentUser']; },
    shopName() { return this.$store.getters['shop/shopName']; },
    roleLabel() {
      const labels = { gerant: 'Gérant', caissier: 'Caissier', support: 'Support', demo: 'Démo' };
      return labels[this.currentUser?.role] || this.currentUser?.role || 'Utilisateur';
    },
    canEditProfile() { return this.currentUser?.role !== 'support'; },
  },
  watch: {
    currentUser: {
      immediate: true,
      handler(user) { this.fillProfileForm(user); },
    },
    visible(isVisible) {
      if (isVisible) this.fillProfileForm(this.currentUser);
    },
  },
  beforeUnmount() { this.revokeAvatarPreview(); },
  methods: {
    fillProfileForm(user) {
      if (!user) return;
      this.profileForm = {
        email: user.email || '',
        telephone1: user.telephone1 || '',
        birth_date: user.birth_date || '',
        gender: user.gender || '',
      };
    },
    handleVisibilityChange(isVisible) {
      if (!isVisible && (this.isSavingProfile || this.isChangingPassword)) return;
      if (!isVisible) this.resetTransientState();
      this.$emit('update:visible', isVisible);
    },
    resetTransientState() {
      this.photoFile = null;
      this.revokeAvatarPreview();
      this.passwordForm = emptyPasswordForm();
      this.fillProfileForm(this.currentUser);
    },
    handlePhotoChange(event) {
      const file = event.target.files?.[0] || null;
      this.photoFile = file;
      this.revokeAvatarPreview();
      if (file) this.avatarPreview = URL.createObjectURL(file);
    },
    sanitizePhoneField(event) {
      this.profileForm.telephone1 = sanitizePhoneInput(event?.target?.value ?? this.profileForm.telephone1);
    },
    async saveProfile() {
      if (this.isSavingProfile || !this.canEditProfile) return;
      if (!isPhoneInputValid(this.profileForm.telephone1)) {
        alert(phoneInputValidationMessage('téléphone du profil'));
        return;
      }
      this.isSavingProfile = true;
      const formData = new FormData();
      formData.append('email', this.profileForm.email || '');
      formData.append('telephone1', sanitizePhoneInput(this.profileForm.telephone1) || '');
      formData.append('birth_date', this.profileForm.birth_date || '');
      formData.append('gender', this.profileForm.gender || '');
      if (this.photoFile) formData.append('profile_photo', this.photoFile);
      try {
        await this.$store.dispatch('auth/updateProfile', formData);
        this.photoFile = null;
        this.revokeAvatarPreview();
        this.fillProfileForm(this.currentUser);
      } finally {
        this.isSavingProfile = false;
      }
    },
    async changePassword() {
      if (this.isChangingPassword) return;
      this.isChangingPassword = true;
      try {
        await this.$store.dispatch('auth/changePassword', { ...this.passwordForm });
        this.passwordForm = emptyPasswordForm();
      } finally {
        this.isChangingPassword = false;
      }
    },
    revokeAvatarPreview() {
      if (!this.avatarPreview) return;
      URL.revokeObjectURL(this.avatarPreview);
      this.avatarPreview = '';
    },
  },
};
</script>

<style scoped>
.profile-card { border-radius: 0.75rem; }
.profile-summary { align-items: center; display: flex; gap: 1rem; margin-bottom: 1rem; }
.profile-avatar { align-items: center; background: rgba(13, 110, 253, 0.1); border: 1px solid rgba(13, 110, 253, 0.18); border-radius: 999px; color: var(--bs-primary); display: inline-flex; flex: 0 0 72px; font-size: 1.4rem; height: 72px; justify-content: center; overflow: hidden; width: 72px; }
.profile-avatar img { height: 100%; object-fit: cover; width: 100%; }
.profile-list dt { color: var(--bs-secondary-color); font-weight: 600; }
.profile-list dd { font-weight: 600; margin-bottom: 0.65rem; }
.profile-form-grid { display: grid; gap: 1rem; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.profile-photo-field, .profile-form-actions { grid-column: 1 / -1; }
.profile-form-actions { display: flex; justify-content: flex-end; }

@media (max-width: 767.98px) {
  :deep(.user-profile-dialog) { height: 100dvh; margin: 0; max-height: 100dvh; width: 100vw !important; }
  :deep(.user-profile-dialog .p-dialog-content) { flex: 1 1 auto; max-height: none !important; }
  .profile-form-grid { grid-template-columns: 1fr; }
  .profile-form-actions { justify-content: stretch; }
  .profile-form-actions .btn { width: 100%; }
}
</style>
